import { MyTrainingsDataCollectionFragment } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

// Defined locally to avoid circular dependency with index.tsx
export type KnowledgeTestMembership = {
  respondentId: number | null
  status: string | undefined
  score: string | null | undefined
}

type Module = NonNullable<
  MyTrainingsDataCollectionFragment['contentModulesConnection']
>['nodes'][number]

const knowledgeTestPassingScore = 50.0

// Helper to check if a knowledge test is passed based on score
const isKnowledgeTestPassed = (test: KnowledgeTestMembership | undefined): boolean => {
  if (!test?.score) return false
  const score = parseFloat(test.score)
  return !isNaN(score) && score >= knowledgeTestPassingScore
}

// Helper to check what completion requirements are configured
const getCompletionRequirements = (item: MyTrainingsDataCollectionFragment) => {
  const completionConfig = item.completionConfiguration

  // Sessions are default completion configuration for a training if none of the configurations
  // were applied or if the attendancePercentage was set
  const isSessionsRequired = !!(
    completionConfig?.attendancePercentage ||
    (!completionConfig?.attendancePercentage &&
      !completionConfig?.moduleCompletedPercentage &&
      !completionConfig?.knowledgeTestRequired)
  )

  const isModulesRequired = !!completionConfig?.moduleCompletedPercentage

  return { isSessionsRequired, isModulesRequired }
}

const getCompletedSessionsPercentage = (item: MyTrainingsDataCollectionFragment): number => {
  const completedSessions =
    item?.sessions?.filter((session) =>
      session.sessionAttendances?.some((attendance) => attendance.status === 'completed')
    ).length ?? 0
  const sessionsCount = item?.sessions?.length ?? 0
  return sessionsCount > 0 ? Math.round((completedSessions / sessionsCount) * 100) : 0
}

// Calculate module progress metrics
const getModulesProgressPercentage = (modules: Module[]): number => {
  // Calculate overall block progress for percentage display
  const allBlocks = modules?.flatMap((module) => module?.contentBlocks ?? [])
  const totalBlocks = allBlocks?.length ?? 0
  const completedBlocks =
    totalBlocks > 0 ? allBlocks?.filter((block) => block?.status === 'completed')?.length : 0
  const moduleProgressPercentage =
    totalBlocks > 0 ? Math.round((completedBlocks / totalBlocks) * 100) : 0
  return moduleProgressPercentage
}

export const getTrainingProgressDescription = (
  item: MyTrainingsDataCollectionFragment,
  knowledgeTests?: KnowledgeTestMembership[]
): string[] | string => {
  const { isSessionsRequired, isModulesRequired } = getCompletionRequirements(item)
  const isKnowledgeTestRequired = item.completionConfiguration?.knowledgeTestRequired ?? false

  const descriptions: string[] = []

  // Sessions progress
  if (isSessionsRequired) {
    const completedSessions =
      item?.sessions?.filter((session) =>
        session?.sessionAttendances?.some((attendance) => attendance?.status === 'completed')
      ).length ?? 0
    const totalSessions = item?.sessions?.length ?? 0

    if (totalSessions > 0) {
      descriptions.push(
        `${i18n.t('trainings.my_trainings_revamp.overview.courses_datatable.sessions_progress', {
          completedSessions,
          totalSessions,
        })}`
      )
    }
  }

  // Modules progress
  if (isModulesRequired) {
    const modules = item?.contentModulesConnection?.nodes ?? []
    const totalModules = modules.length
    const completedModules = modules?.filter((module) => {
      const blocks = module?.contentBlocks ?? []
      return blocks.length > 0 && blocks.every((block) => block?.status === 'completed')
    }).length

    if (totalModules > 0) {
      descriptions.push(
        `${i18n.t('trainings.my_trainings_revamp.overview.courses_datatable.modules_progress', {
          completedModules,
          totalModules,
        })}`
      )
    }
  }

  // Knowledge test progress
  if (isKnowledgeTestRequired) {
    const passedTest = knowledgeTests?.find((test) => isKnowledgeTestPassed(test))
    const testStatus = passedTest ? 'completed' : 'not completed'
    descriptions.push(
      `${i18n.t(
        'trainings.my_trainings_revamp.overview.courses_datatable.knowledge_test_progress',
        {
          testStatus,
        }
      )}`
    )
  }

  return descriptions.length > 0
    ? descriptions
    : i18n.t('trainings.my_trainings_revamp.overview.courses_datatable.no_progress')
}
export const calculateTrainingProgressPercentage = (
  item: MyTrainingsDataCollectionFragment,
  knowledgeTests?: KnowledgeTestMembership[]
) => {
  // Find a passed test, or use the first one if none passed
  const passedTest = knowledgeTests?.find((test) => isKnowledgeTestPassed(test))
  const knowledgeTest = passedTest ?? knowledgeTests?.[0]

  return getTrainingProgressPercentage(item, knowledgeTest)
}

const getTrainingProgressPercentage = (
  item: MyTrainingsDataCollectionFragment,
  knowledgeTest?: KnowledgeTestMembership
): number => {
  const { isSessionsRequired, isModulesRequired } = getCompletionRequirements(item)
  const isKnowledgeTestRequired = !!knowledgeTest
  const modules = item?.contentModulesConnection?.nodes ?? []
  const completedKnowledgeTestPercentage = isKnowledgeTestRequired
    ? isKnowledgeTestPassed(knowledgeTest)
      ? 100
      : 0
    : 0

  // Case 1: Only sessions are required
  if (isSessionsRequired && !isModulesRequired && !isKnowledgeTestRequired) {
    return getCompletedSessionsPercentage(item)
  }

  // Case 2: Only modules are required
  if (isModulesRequired && !isSessionsRequired && !isKnowledgeTestRequired) {
    return getModulesProgressPercentage(modules)
  }

  // case 3: only knowledge test is required
  if (isKnowledgeTestRequired && !isModulesRequired && !isSessionsRequired) {
    return isKnowledgeTestPassed(knowledgeTest) ? 100 : 0
  }

  // Case 4: Both modules and sessions are required
  if (isModulesRequired && isSessionsRequired && !isKnowledgeTestRequired) {
    return Math.round(
      (getCompletedSessionsPercentage(item) + getModulesProgressPercentage(modules)) / 2
    )
  }

  //Case 5: Modules and Knowledge test are required
  if (isModulesRequired && isKnowledgeTestRequired && !isSessionsRequired) {
    return Math.round(
      (getModulesProgressPercentage(modules) + completedKnowledgeTestPercentage) / 2
    )
  }

  //Case 6: Sessions and Knowledge test are required
  if (isSessionsRequired && isKnowledgeTestRequired && !isModulesRequired) {
    return Math.round((getCompletedSessionsPercentage(item) + completedKnowledgeTestPercentage) / 2)
  }
  //Case 7: All requirements are required
  if (isSessionsRequired && isModulesRequired && isKnowledgeTestRequired) {
    return Math.round(
      (getCompletedSessionsPercentage(item) +
        getModulesProgressPercentage(modules) +
        completedKnowledgeTestPercentage) /
        3
    )
  }
  return 0
}
